import { useNostr } from '@nostrify/react';
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { useCurrentUser } from './useCurrentUser';
import { discoverRelayList, resolveReadRelaysFor } from '@/lib/outbox';
import { FALLBACK_RELAYS } from '@/lib/blogConfig';

type EventTemplate = Pick<NostrEvent, 'kind' | 'content'> &
  Partial<Pick<NostrEvent, 'tags' | 'created_at'>>;

/**
 * Publish an event following the outbox model:
 * - Sign with the logged-in user's signer.
 * - Publish to the AUTHOR's write relays (so it lives where their content lives).
 * - Also publish to the READ relays of every `p`-tagged user (their inboxes),
 *   so mentioned users receive it.
 */
export function useOutboxPublish(): UseMutationResult<
  NostrEvent,
  Error,
  EventTemplate
> {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async (t: EventTemplate) => {
      if (!user) throw new Error('User is not logged in');

      const tags = t.tags ?? [];
      if (
        location.protocol === 'https:' &&
        !tags.some(([name]) => name === 'client')
      ) {
        tags.push(['client', location.hostname]);
      }

      const event = await user.signer.signEvent({
        kind: t.kind,
        content: t.content ?? '',
        tags,
        created_at: t.created_at ?? Math.floor(Date.now() / 1000),
      });

      // Author's write relays.
      const authorRelays = await discoverRelayList(nostr, user.pubkey).catch(
        () => ({ write: [...FALLBACK_RELAYS], read: [...FALLBACK_RELAYS] }),
      );

      // Read relays of every p-tagged pubkey (recipients' inboxes).
      const taggedPubkeys = [
        ...new Set(
          event.tags
            .filter(([n]) => n === 'p' || n === 'P')
            .map(([, pk]) => pk)
            .filter(Boolean),
        ),
      ];
      const recipientRelays = await resolveReadRelaysFor(nostr, taggedPubkeys).catch(
        () => [],
      );

      const targets = [
        ...new Set([
          ...authorRelays.write,
          ...recipientRelays,
          ...FALLBACK_RELAYS,
        ]),
      ];

      const group = nostr.group(targets);
      await group.event(event, { signal: AbortSignal.timeout(6000) });
      return event;
    },
    onError: (error) => {
      console.error('Failed to publish event:', error);
    },
  });
}
